import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import OtpInput from "./OtpInput";
import { markSignedIn } from "./api";
import LanguageToggle from "./LanguageToggle";
import UpdateBanner from "./UpdateBanner";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

type Method = "totp" | "email" | "backup";

const Login: React.FC = () => {
  const { t } = useTranslation();
  const methodLabels: Record<Method, string> = {
    totp: t("login.methodTotp"),
    email: t("login.methodEmail"),
    backup: t("login.methodBackup"),
  };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Step-2 (2FA) state
  const [loginToken, setLoginToken] = useState<string | null>(null);
  const [methods, setMethods] = useState<Method[]>([]);
  const [method, setMethod] = useState<Method>("totp");
  const [code, setCode] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail && localStorage.getItem("rememberMe") === "true") {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const finishLogin = (data: { user: unknown }) => {
    if (rememberMe) {
      localStorage.setItem("rememberedEmail", email);
      localStorage.setItem("rememberMe", "true");
    } else {
      localStorage.removeItem("rememberedEmail");
      localStorage.removeItem("rememberMe");
    }
    // No token to store: the session is an httpOnly cookie the server set on
    // this response, which this code deliberately cannot read. `user` is
    // non-secret UI state, and markSignedIn is just a render hint.
    markSignedIn();
    localStorage.setItem("user", JSON.stringify(data.user));
    window.location.href = "/";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "ReviewCounter" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t("login.loginFailed"));
        setLoading(false);
        return;
      }
      if (data.twoFactorRequired) {
        setLoginToken(data.loginToken);
        setMethods(data.methods);
        setMethod(data.methods[0]);
        setLoading(false);
        return;
      }
      finishLogin(data);
    } catch {
      setError(t("login.networkError"));
      setLoading(false);
    }
  };

  const sendEmailCode = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/login/2fa/send-email`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "ReviewCounter" },
        body: JSON.stringify({ loginToken }),
      });
      const data = await res.json();
      setEmailSent(true);
      if (data.devCode) setError(t("login.devModeCode", { code: data.devCode }));
    } catch {
      setError(t("login.couldNotSendCode"));
    }
    setLoading(false);
  };

  const verify2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/login/2fa/verify`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "ReviewCounter" },
        body: JSON.stringify({ loginToken, method, code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t("login.invalidCode"));
        setLoading(false);
        return;
      }
      finishLogin(data);
    } catch {
      setError(t("login.networkError"));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50">
      {/* An installed app lands here on every session expiry, with no address
          bar to reload from - the one place outside MainLayout where a stale
          bundle is a dead end rather than an inconvenience. */}
      <div className="absolute top-0 inset-x-0"><UpdateBanner /></div>
      <div className="absolute top-4 right-4">
        <LanguageToggle />
      </div>
      <img
        src="/reviewcounter-logo.png"
        alt="ReviewCounter"
        className="w-52 max-w-xs mb-6 object-contain"
      />

      {!loginToken ? (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl shadow max-w-sm w-full space-y-4">
          <h1 className="text-2xl font-bold text-center text-teal-600">{t("login.signIn")}</h1>
          <input
            className="w-full border rounded p-2"
            type="email"
            placeholder={t("login.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
          <input
            className="w-full border rounded p-2"
            type="password"
            placeholder={t("login.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          <div className="flex items-center space-x-2">
            <input
              id="rememberMe"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 text-teal-600 border-gray-300 rounded cursor-pointer"
            />
            <label htmlFor="rememberMe" className="text-sm text-gray-700 cursor-pointer">
              {t("login.rememberMe")}
            </label>
          </div>
          {error && <div className="text-red-600 text-sm text-center">{error}</div>}
          <button
            type="submit"
            className="w-full bg-teal-600 text-white py-2 rounded font-semibold hover:bg-teal-700 transition"
            disabled={loading}
          >
            {loading ? t("login.signingIn") : t("login.signIn")}
          </button>
          <div className="text-center">
            <a href="/forgot-password" className="text-teal-600 hover:text-teal-800 text-sm underline">
              {t("login.forgotPassword")}
            </a>
          </div>
        </form>
      ) : (
        <form onSubmit={verify2fa} className="bg-white p-6 rounded-xl shadow max-w-sm w-full space-y-4">
          <h1 className="text-xl font-bold text-center text-teal-600">{t("login.twoFactorTitle")}</h1>

          {methods.length > 1 && (
            <div className="flex gap-2 flex-wrap justify-center">
              {methods.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setMethod(m);
                    setCode("");
                    setError(null);
                    setEmailSent(false);
                  }}
                  className={`text-xs px-3 py-1 rounded-full border ${
                    method === m ? "bg-teal-600 text-white border-teal-600" : "bg-white text-gray-700"
                  }`}
                >
                  {methodLabels[m]}
                </button>
              ))}
            </div>
          )}

          {method === "email" && !emailSent ? (
            <button
              type="button"
              onClick={sendEmailCode}
              disabled={loading}
              className="w-full bg-teal-600 text-white py-2 rounded font-semibold disabled:opacity-50"
            >
              {loading ? t("login.sending") : t("login.sendEmailCode")}
            </button>
          ) : (
            <>
              <p className="text-sm text-gray-600 text-center">
                {method === "totp" && t("login.enterTotp")}
                {method === "email" && t("login.enterEmailCode")}
                {method === "backup" && t("login.enterBackupCode")}
              </p>
              {method === "backup" ? (
                <input
                  autoFocus
                  className="w-full border rounded p-2 tracking-widest text-center uppercase"
                  placeholder="XXXXX-XXXXX"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              ) : (
                <OtpInput value={code} onChange={setCode} disabled={loading} />
              )}
            </>
          )}

          {error && <div className="text-red-600 text-sm text-center">{error}</div>}
          <button
            type="submit"
            className="w-full bg-green-600 text-white py-2 rounded font-semibold disabled:opacity-50"
            disabled={loading || !code.trim() || (method === "email" && !emailSent)}
          >
            {loading ? t("login.verifying") : t("login.verifyAndSignIn")}
          </button>
          <button
            type="button"
            onClick={() => {
              setLoginToken(null);
              setCode("");
              setError(null);
              setEmailSent(false);
            }}
            className="w-full text-sm text-gray-500 underline"
          >
            {t("login.back")}
          </button>
        </form>
      )}
    </div>
  );
};

export default Login;
